import { useRef, useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Download, QrCode } from 'lucide-react';

interface ShareQRCodeProps {
  shareUrl: string;
  sessionTitle?: string;
}

export function ShareQRCode({ shareUrl, sessionTitle }: ShareQRCodeProps) {
  const qrRef = useRef<HTMLDivElement>(null);
  const [logoLoaded, setLogoLoaded] = useState(false);

  // Logo path - using the existing VoiceThera logo
  const logoSrc = '/lovable-uploads/voicethera-logo-new.png';

  // Preload logo to ensure it's ready for canvas rendering
  useEffect(() => {
    const img = new Image();
    img.onload = () => setLogoLoaded(true);
    img.onerror = () => setLogoLoaded(true); // Continue without logo if it fails
    img.src = logoSrc;
  }, []);

  const downloadQRCode = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;

    // Create a high-resolution version for download
    const downloadCanvas = document.createElement('canvas');
    const downloadSize = 512;
    downloadCanvas.width = downloadSize;
    downloadCanvas.height = downloadSize;
    const ctx = downloadCanvas.getContext('2d');
    if (!ctx) return;

    // Draw the QR code scaled up
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(canvas, 0, 0, downloadSize, downloadSize);

    // Draw the logo on top (centered)
    const logo = new Image();
    logo.crossOrigin = 'anonymous';
    logo.onload = () => {
      const logoSize = downloadSize * 0.2; // 20% of QR code size
      const logoX = (downloadSize - logoSize) / 2;
      const logoY = (downloadSize - logoSize) / 2;

      // Draw white background behind logo for better visibility
      ctx.fillStyle = 'white';
      ctx.beginPath();
      ctx.arc(downloadSize / 2, downloadSize / 2, logoSize / 2 + 4, 0, Math.PI * 2);
      ctx.fill();

      // Draw logo
      ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);

      // Trigger download
      triggerDownload(downloadCanvas);
    };
    logo.onerror = () => {
      // Download without logo if it fails to load
      triggerDownload(downloadCanvas);
    };
    logo.src = logoSrc;
  };

  const triggerDownload = (canvas: HTMLCanvasElement) => {
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    const sanitizedTitle = (sessionTitle || 'meditation')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    link.download = `voicethera-${sanitizedTitle}-qr.png`;
    link.href = url;
    link.click();
  };

  return (
    <div className="flex flex-col items-center gap-4 p-4 bg-muted/50 rounded-lg">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <QrCode className="h-4 w-4" />
        <span>Scan to listen</span>
      </div>

      <div
        ref={qrRef}
        className="bg-white p-3 rounded-lg shadow-sm relative"
      >
        <QRCodeCanvas
          value={shareUrl}
          size={200}
          level="H" // High error correction for logo overlay
          includeMargin={true}
          imageSettings={{
            src: logoSrc,
            height: 40,
            width: 40,
            excavate: true, // Removes QR modules behind the logo
          }}
        />
      </div>

      <Button
        onClick={downloadQRCode}
        variant="outline"
        size="sm"
        className="gap-2"
      >
        <Download className="h-4 w-4" />
        Download QR Code
      </Button>
    </div>
  );
}

export default ShareQRCode;
